/*
 * Store Locator — behaviour for sections/store-locator.liquid.
 *
 * - Loads retailers from the JSON asset referenced by data-data-url
 *   (generated by scripts/store-locator-sync.py).
 * - Default view: every retailer, A–Z, paginated client-side.
 * - Search: geocode the query (Google Maps JS Geocoder if a key is configured,
 *   else OpenStreetMap Nominatim), then sort by haversine distance and keep
 *   retailers within the configured radius (falling back to the closest few).
 * - Keeps ?q= and ?page= in the URL (history.replaceState) so reload/back
 *   restores the same view.
 *
 * Accessibility contract (see the section file header):
 *   - After a search: focus moves to the visible results summary. No live
 *     announcement for the same event.
 *   - After pagination: focus stays on the equivalent control in the nav; the
 *     status region announces "Page X of Y. Showing retailers A to B of N."
 *   - Geocode failures are inline form errors (aria-invalid + aria-describedby +
 *     focus on the input). Network/service failures are announced via status
 *     and shown as a visible notice; focus is not moved.
 *   - Prev/Next use aria-disabled (remain focusable). Current page uses
 *     aria-current="page".
 *   - Smooth scrolling is disabled under prefers-reduced-motion.
 */
(function () {
  'use strict';

  var EARTH_RADIUS_MI = 3958.7613;
  var NEAREST_FALLBACK = 8;
  var SEARCHING_DELAY_MS = 500;
  var ZIP_RE = /^\s*(\d{5})(?:-\d{4})?\s*$/;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function t(template, vars) {
    return String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, key) {
      return vars && Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : '';
    });
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (attrs[k] === null || attrs[k] === undefined || attrs[k] === false) return;
        if (k === 'class') node.className = attrs[k];
        else if (k === 'text') node.textContent = attrs[k];
        else node.setAttribute(k, attrs[k]);
      });
    }
    (children || []).forEach(function (c) {
      if (c === null || c === undefined || c === false) return;
      node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return node;
  }

  function toRad(deg) { return (deg * Math.PI) / 180; }

  function haversineMiles(lat1, lng1, lat2, lng2) {
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * EARTH_RADIUS_MI * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function formatDistance(mi) {
    if (mi < 10) return (Math.round(mi * 10) / 10).toFixed(1);
    return String(Math.round(mi));
  }

  function fullAddress(store) {
    var line2 = [store.city, store.state].filter(Boolean).join(', ');
    if (store.zip) line2 = (line2 + ' ' + store.zip).trim();
    return [store.name, store.address, store.address2, line2].filter(Boolean).join(', ');
  }

  function digitsForTel(phone) {
    var d = String(phone).replace(/[^\d+]/g, '');
    return d;
  }

  /* ---------------------------------------------------------------- Geocoding */

  function geocodeGoogle(query, key, countryCodes) {
    return loadGoogleMaps(key).then(function () {
      return window.google.maps.importLibrary('geocoding');
    }).then(function (lib) {
      var geocoder = new lib.Geocoder();
      var request = { address: query };
      if (countryCodes[0]) request.region = countryCodes[0];
      return geocoder.geocode(request).then(function (res) {
        var r = res && res.results && res.results[0];
        if (!r) return null;
        return {
          lat: r.geometry.location.lat(),
          lng: r.geometry.location.lng(),
          label: shortGoogleLabel(r) || r.formatted_address || query
        };
      }, function (err) {
        // ZERO_RESULTS surfaces as a rejection in the promise API.
        var code = err && (err.code || err.status || (err.message || ''));
        if (String(code).indexOf('ZERO_RESULTS') !== -1) return null;
        throw err;
      });
    });
  }

  function shortGoogleLabel(result) {
    var parts = { locality: '', admin1: '', postal: '' };
    (result.address_components || []).forEach(function (c) {
      var types = c.types || [];
      if (types.indexOf('locality') !== -1 || types.indexOf('postal_town') !== -1) parts.locality = parts.locality || c.long_name;
      else if (types.indexOf('sublocality') !== -1 && !parts.locality) parts.locality = c.long_name;
      else if (types.indexOf('administrative_area_level_1') !== -1) parts.admin1 = c.short_name;
      else if (types.indexOf('postal_code') !== -1) parts.postal = c.long_name;
    });
    var s = [parts.locality, parts.admin1].filter(Boolean).join(', ');
    if (parts.postal) s = (s + ' ' + parts.postal).trim();
    return s;
  }

  var googleLoader = null;
  function loadGoogleMaps(key) {
    if (window.google && window.google.maps && window.google.maps.importLibrary) return Promise.resolve();
    if (googleLoader) return googleLoader;
    googleLoader = new Promise(function (resolve, reject) {
      // Official dynamic-library-import bootstrap (loads only the libraries requested).
      /* eslint-disable */
      (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=`https://maps.${c}apis.com/maps/api/js?`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({ key: key, v: 'weekly' });
      /* eslint-enable */
      if (window.google && window.google.maps && window.google.maps.importLibrary) resolve();
      else reject(new Error('Google Maps bootstrap failed'));
    });
    return googleLoader;
  }

  function geocodeNominatim(query, countryCodes) {
    var params = new URLSearchParams({ format: 'jsonv2', limit: '1', addressdetails: '1' });
    var zip = ZIP_RE.exec(query);
    if (zip && countryCodes.length) {
      params.set('postalcode', zip[1]);
      params.set('country', countryCodes[0]);
    } else {
      params.set('q', query);
      if (countryCodes.length) params.set('countrycodes', countryCodes.join(','));
    }
    var url = 'https://nominatim.openstreetmap.org/search?' + params.toString();
    return fetch(url, { headers: { 'Accept': 'application/json' } }).then(function (res) {
      if (!res.ok) throw new Error('Nominatim HTTP ' + res.status);
      return res.json();
    }).then(function (rows) {
      var r = rows && rows[0];
      if (!r) return null;
      var a = r.address || {};
      var locality = a.city || a.town || a.village || a.hamlet || a.municipality || a.county || '';
      var label = [locality, a.state].filter(Boolean).join(', ');
      if (a.postcode) label = (label + ' ' + a.postcode).trim();
      return { lat: parseFloat(r.lat), lng: parseFloat(r.lon), label: label || r.display_name || query };
    });
  }

  /* --------------------------------------------------------------- Component */

  function StoreLocator(root) {
    this.root = root;
    this.dataUrl = root.getAttribute('data-data-url');
    this.perPage = parseInt(root.getAttribute('data-per-page'), 10) || 16;
    this.radius = parseInt(root.getAttribute('data-radius'), 10) || 100;
    this.googleKey = (root.getAttribute('data-google-key') || '').trim();
    this.countryCodes = (root.getAttribute('data-country-codes') || 'us').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    try { this.strings = JSON.parse(root.getAttribute('data-strings') || '{}'); } catch (e) { this.strings = {}; }

    var q = function (sel) { return root.querySelector(sel); };
    this.form = q('[data-store-locator-form]');
    this.input = q('[data-store-locator-input]');
    this.submit = q('[data-store-locator-submit]');
    this.error = q('[data-store-locator-error]');
    this.notice = q('[data-store-locator-notice]');
    this.resetWrap = q('[data-store-locator-reset-wrap]');
    this.resetBtn = q('[data-store-locator-reset]');
    this.status = q('[data-store-locator-status]');
    this.results = q('[data-store-locator-results]');
    this.summary = q('[data-store-locator-summary]');
    this.list = q('[data-store-locator-list]');
    this.pagination = q('[data-store-locator-pagination]');
    this.pageOf = q('[data-store-locator-page-of]');
    this.pages = q('[data-store-locator-pages]');

    this.stores = [];
    this.view = { mode: 'all', items: [], page: 1, place: '', query: '' };
    this.busy = false;

    this.bind();
    this.load();
  }

  StoreLocator.prototype.s = function (key, vars) { return t(this.strings[key], vars); };

  StoreLocator.prototype.bind = function () {
    var self = this;
    this.form.addEventListener('submit', function (e) {
      e.preventDefault();
      self.search(self.input.value);
    });
    this.input.addEventListener('input', function () {
      if (self.input.getAttribute('aria-invalid') === 'true') self.clearError();
    });
    this.resetBtn.addEventListener('click', function () {
      self.input.value = '';
      self.clearError();
      self.hideNotice();
      self.showAll({ focusSummary: true });
      self.syncUrl();
    });
    this.pages.addEventListener('click', function (e) {
      var btn = e.target.closest('button[data-page], button[data-nav]');
      if (!btn) return;
      if (btn.getAttribute('aria-disabled') === 'true' || btn.getAttribute('aria-current') === 'page') return;
      var target;
      if (btn.hasAttribute('data-page')) target = parseInt(btn.getAttribute('data-page'), 10);
      else target = self.view.page + (btn.getAttribute('data-nav') === 'next' ? 1 : -1);
      // Scroll the results into view only for pointer activation (e.detail > 0);
      // keyboard users keep the focused control on screen (WCAG 2.4.7).
      self.goToPage(target, btn.hasAttribute('data-page') ? { page: target } : { nav: btn.getAttribute('data-nav') }, { scroll: e.detail > 0 });
    });
  };

  StoreLocator.prototype.load = function () {
    var self = this;
    this.setSummary(this.s('loading_data'));
    fetch(this.dataUrl, { credentials: 'omit' }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    }).then(function (data) {
      var stores = (data && data.stores) || [];
      self.stores = stores.filter(function (s) {
        return s && s.name && isFinite(parseFloat(s.lat)) && isFinite(parseFloat(s.lng));
      }).map(function (s) {
        s.lat = parseFloat(s.lat); s.lng = parseFloat(s.lng);
        return s;
      });
      self.stores.sort(function (a, b) {
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) ||
          String(a.state).localeCompare(String(b.state)) || String(a.city).localeCompare(String(b.city));
      });
      self.restoreFromUrl();
    }).catch(function () {
      self.setSummary(self.s('data_error'));
      self.announce(self.s('data_error'));
    });
  };

  StoreLocator.prototype.restoreFromUrl = function () {
    var params = new URLSearchParams(window.location.search);
    var q = (params.get('q') || '').trim();
    var page = parseInt(params.get('page'), 10) || 1;
    if (q) {
      this.input.value = q;
      this.search(q, { page: page, restoring: true });
    } else {
      this.showAll({ page: page });
    }
  };

  /* ---- View state */

  StoreLocator.prototype.showAll = function (opts) {
    opts = opts || {};
    this.view = { mode: 'all', items: this.stores, page: opts.page || 1, place: '', query: '' };
    this.resetWrap.hidden = true;
    this.render({ focusSummary: !!opts.focusSummary });
  };

  StoreLocator.prototype.search = function (rawQuery, opts) {
    opts = opts || {};
    var self = this;
    var query = String(rawQuery || '').trim();
    this.clearError();
    this.hideNotice();
    if (!query) {
      this.showError(this.s('error_empty'));
      return;
    }
    if (this.busy) return;
    this.busy = true;
    this.results.setAttribute('aria-busy', 'true');
    var searchingTimer = window.setTimeout(function () { self.announce(self.s('searching')); }, SEARCHING_DELAY_MS);

    var geocode = this.googleKey
      ? geocodeGoogle(query, this.googleKey, this.countryCodes)
      : geocodeNominatim(query, this.countryCodes);

    geocode.then(function (loc) {
      window.clearTimeout(searchingTimer);
      if (!loc) {
        self.showError(self.s('error_not_found', { query: query }));
        return;
      }
      var items = self.stores.map(function (s) {
        return { store: s, distance: haversineMiles(loc.lat, loc.lng, s.lat, s.lng) };
      }).sort(function (a, b) { return a.distance - b.distance; });
      var within = items.filter(function (i) { return i.distance <= self.radius; });
      var mode = 'near';
      if (!within.length) {
        mode = 'nearest';
        within = items.slice(0, NEAREST_FALLBACK);
      }
      self.view = { mode: mode, items: within, page: opts.page || 1, place: loc.label, query: query };
      self.resetWrap.hidden = false;
      self.render({ focusSummary: !opts.restoring });
      self.syncUrl();
    }).catch(function () {
      window.clearTimeout(searchingTimer);
      var msg = self.s('error_unavailable');
      self.showNotice(msg);
      self.announce(msg);
    }).then(function () {
      self.busy = false;
      self.results.removeAttribute('aria-busy');
    });
  };

  StoreLocator.prototype.goToPage = function (page, focusTarget, opts) {
    opts = opts || {};
    var total = this.totalPages();
    page = Math.min(Math.max(1, page), total);
    if (page === this.view.page) return;
    this.view.page = page;
    this.render({ focusTarget: focusTarget, announcePage: true, scroll: opts.scroll !== false });
    this.syncUrl();
  };

  StoreLocator.prototype.totalPages = function () {
    return Math.max(1, Math.ceil(this.view.items.length / this.perPage));
  };

  StoreLocator.prototype.syncUrl = function () {
    if (!window.history || !window.history.replaceState) return;
    var url = new URL(window.location.href);
    if (this.view.mode === 'all') url.searchParams.delete('q');
    else url.searchParams.set('q', this.view.query);
    if (this.view.page > 1) url.searchParams.set('page', String(this.view.page));
    else url.searchParams.delete('page');
    window.history.replaceState(null, '', url.toString());
  };

  /* ---- Rendering */

  StoreLocator.prototype.render = function (opts) {
    opts = opts || {};
    var v = this.view;
    var total = v.items.length;
    var pages = this.totalPages();
    v.page = Math.min(Math.max(1, v.page), pages);
    var from = total ? (v.page - 1) * this.perPage + 1 : 0;
    var to = Math.min(v.page * this.perPage, total);
    var slice = v.items.slice(from - 1 < 0 ? 0 : from - 1, to);

    // Summary text (visible + used verbatim for announcements).
    var vars = { count: total, from: from, to: to, radius: this.radius, place: v.place, page: v.page, pages: pages };
    var summaryText;
    if (v.mode === 'all') summaryText = this.s('summary_all', vars);
    else if (v.mode === 'nearest') summaryText = this.s('summary_nearest', vars);
    else if (total === 1) summaryText = this.s('summary_results_one', vars);
    else if (total === 0) summaryText = this.s('summary_none', vars);
    else summaryText = this.s('summary_results_other', vars);
    this.setSummary(summaryText);

    // List
    var self = this;
    var frag = document.createDocumentFragment();
    slice.forEach(function (item) {
      var store = v.mode === 'all' ? item : item.store;
      var distance = v.mode === 'all' ? null : item.distance;
      frag.appendChild(self.renderStore(store, distance));
    });
    this.list.textContent = '';
    this.list.appendChild(frag);

    // Pagination
    this.renderPagination(v.page, pages);

    // Focus / announce
    if (opts.focusSummary) {
      this.scrollTo(this.summary);
      this.summary.focus({ preventScroll: true });
    } else if (opts.focusTarget) {
      var target = null;
      if (opts.focusTarget.page) target = this.pages.querySelector('button[data-page="' + opts.focusTarget.page + '"]');
      else if (opts.focusTarget.nav) target = this.pages.querySelector('button[data-nav="' + opts.focusTarget.nav + '"]');
      if (target) target.focus({ preventScroll: true });
    }
    if (opts.announcePage) {
      this.announce(this.s('page_status', vars));
    }
    if (opts.scroll) this.scrollTo(this.results);
  };

  StoreLocator.prototype.renderStore = function (store, distance) {
    var s = this.strings;
    var line2 = [store.city, store.state].filter(Boolean).join(', ');
    if (store.zip) line2 = (line2 + ' ' + store.zip).trim();

    var children = [el('h3', { class: 'store-locator__store-name', text: store.name })];
    if (distance !== null && distance !== undefined) {
      children.push(el('p', { class: 'store-locator__store-distance', text: t(s.distance, { distance: formatDistance(distance) }) }));
    }
    if (store.address) children.push(el('p', { class: 'store-locator__store-address', text: store.address }));
    if (store.address2) children.push(el('p', { class: 'store-locator__store-address', text: store.address2 }));
    if (line2) children.push(el('p', { class: 'store-locator__store-address', text: line2 }));
    if (store.phone) {
      children.push(el('p', { class: 'store-locator__store-phone' }, [
        el('a', { href: 'tel:' + digitsForTel(store.phone) }, [
          el('span', { class: 'visually-hidden', text: s.phone + ' ' }),
          store.phone
        ])
      ]));
    }
    if (store.hours) {
      children.push(el('p', { class: 'store-locator__store-hours' }, [
        el('span', { class: 'visually-hidden', text: s.hours + ' ' }),
        store.hours
      ]));
    }
    var links = [];
    links.push(el('a', {
      class: 'store-locator__store-link',
      href: 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(fullAddress(store))
    }, [s.directions, el('span', { class: 'visually-hidden', text: ' ' + t(s.directions_to, { name: store.name }) })]));
    if (store.website) {
      links.push(el('a', { class: 'store-locator__store-link', href: store.website, rel: 'noopener' }, [
        s.website, el('span', { class: 'visually-hidden', text: ' ' + t(s.website_for, { name: store.name }) })
      ]));
    }
    children.push(el('p', { class: 'store-locator__store-links' }, links));

    return el('li', { class: 'store-locator__store' }, children);
  };

  StoreLocator.prototype.pageNumbers = function (current, total) {
    var set = {};
    var add = function (n) { if (n >= 1 && n <= total) set[n] = true; };
    if (total <= 7) {
      for (var i = 1; i <= total; i++) add(i);
    } else {
      add(1); add(total);
      if (current <= 4) { for (var j = 1; j <= 5; j++) add(j); }
      else if (current >= total - 3) { for (var k = total - 4; k <= total; k++) add(k); }
      else { add(current - 1); add(current); add(current + 1); }
    }
    return Object.keys(set).map(Number).sort(function (a, b) { return a - b; });
  };

  StoreLocator.prototype.renderPagination = function (current, total) {
    var s = this.strings;
    if (total <= 1) {
      this.pagination.hidden = true;
      this.pages.textContent = '';
      return;
    }
    this.pagination.hidden = false;
    this.pageOf.textContent = t(s.page_of, { page: current, pages: total });

    var items = [];
    // Visible "Previous"/"Next" (general.pagination.*); accessible name is the
    // full "Previous page"/"Next page" via aria-label (visible text is contained
    // in the name, so 2.5.3 Label in Name holds).
    items.push(el('li', null, [el('button', {
      type: 'button', class: 'store-locator__page-btn store-locator__page-btn--nav',
      'data-nav': 'prev', 'aria-label': s.previous_page, 'aria-disabled': current === 1 ? 'true' : null,
      text: s.previous
    })]));

    var numbers = this.pageNumbers(current, total);
    var prev = 0;
    numbers.forEach(function (n) {
      if (n - prev > 1) items.push(el('li', null, [el('span', { class: 'store-locator__ellipsis', 'aria-hidden': 'true', text: '…' })]));
      items.push(el('li', null, [el('button', {
        type: 'button', class: 'store-locator__page-btn', 'data-page': String(n),
        'aria-current': n === current ? 'page' : null
      }, [el('span', { class: 'visually-hidden', text: s.page + ' ' }), String(n)])]));
      prev = n;
    });

    items.push(el('li', null, [el('button', {
      type: 'button', class: 'store-locator__page-btn store-locator__page-btn--nav',
      'data-nav': 'next', 'aria-label': s.next_page, 'aria-disabled': current === total ? 'true' : null,
      text: s.next
    })]));

    this.pages.textContent = '';
    items.forEach(function (li) { this.pages.appendChild(li); }, this);
  };

  /* ---- Messaging helpers */

  StoreLocator.prototype.setSummary = function (text) {
    this.summary.textContent = text;
    this.summary.hidden = !text;
  };

  StoreLocator.prototype.announce = function (text) {
    var region = this.status;
    region.textContent = '';
    window.requestAnimationFrame(function () { region.textContent = text; });
  };

  StoreLocator.prototype.showError = function (text) {
    this.error.textContent = text;
    this.error.hidden = false;
    this.input.setAttribute('aria-invalid', 'true');
    this.input.setAttribute('aria-describedby', this.error.id);
    // If focus is already in the field (Enter key), a focus() call is a no-op and
    // the new describedby text is never read — announce it instead. Otherwise
    // move focus to the field so the error is read with it.
    if (document.activeElement === this.input) this.announce(text);
    else this.input.focus();
  };

  StoreLocator.prototype.clearError = function () {
    this.error.textContent = '';
    this.error.hidden = true;
    this.input.removeAttribute('aria-invalid');
    this.input.removeAttribute('aria-describedby');
  };

  StoreLocator.prototype.showNotice = function (text) {
    this.notice.textContent = text;
    this.notice.hidden = false;
  };

  StoreLocator.prototype.hideNotice = function () {
    this.notice.textContent = '';
    this.notice.hidden = true;
  };

  StoreLocator.prototype.scrollTo = function (node) {
    if (!node || !node.getBoundingClientRect) return;
    var top = node.getBoundingClientRect().top;
    // Only scroll if the target is hidden under the sticky header (its
    // scroll-margin-top clearance) or below the top third of the viewport.
    var clearance = parseFloat(window.getComputedStyle(node).scrollMarginTop) || 0;
    if (top >= clearance && top < window.innerHeight * 0.33) return;
    var behavior = reduceMotion && reduceMotion.matches ? 'auto' : 'smooth';
    try { node.scrollIntoView({ behavior: behavior, block: 'start' }); } catch (e) { node.scrollIntoView(true); }
  };

  /* -------------------------------------------------------------------- Init */

  var instances = new WeakMap();
  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-section-type="store-locator"]');
    Array.prototype.forEach.call(roots, function (root) {
      if (instances.has(root)) return;
      instances.set(root, new StoreLocator(root));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  // Theme editor: re-init when the section is (re)loaded.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
